import { apiClient } from './apiClient';

// ===============================
// INTERFACES
// ===============================

export interface DatosPorDia {
  fecha: string;
  maquina_id: number;
  maquina_codigo: string;
  horas: number;
}

export interface DatosUso {
  maquina_id: number;
  maquina_codigo: string;
  horas_usadas: number;
  horas_disponibles: number;
  porcentaje_uso: number;
}

export interface DatosConsumo {
  fecha: string;
  insumo_codigo: string;
  insumo_descripcion: string;
  cantidad_consumida: number;
  unidad_medida: string;
}

export interface ResumenMaquina {
  total_horas: number;
  maquinas_activas: number;
  horas_promedio: number;
}

export interface ResumenConsumo {
  total_consumido: number;
  insumo_mas_consumido: string;
  cantidad_mas_consumida: number;
}

export interface DatosEficienciaCombustible {
  reporte_id: number;
  fecha: string;
  maquina_codigo: string;
  maquina_descripcion: string;
  operador_codigo: string;
  operador_descripcion: string;
  faena_codigo: string;
  faena_descripcion: string;
  horas_trabajadas: number;
  area_trabajada: number;
  consumo_petroleo: number;
  consumo_por_hora: number;
}

// ===============================
// SERVICE
// ===============================

class GestionService {
  /**
   * Obtiene datos de horas por día para el calendario
   */
  async obtenerDatosCalendario(
    fechaInicio: string,
    fechaFin: string
  ): Promise<DatosPorDia[]> {
    try {
      const response = await apiClient.get<DatosPorDia[]>('/gestion/calendario', {
        params: {
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  /**
   * Obtiene datos de uso de máquinas (horas usadas vs disponibles)
   */
  async obtenerDatosUso(
    horasDisponiblesParam?: number
  ): Promise<DatosUso[]> {
    try {
      const response = await apiClient.get<DatosUso[]>('/gestion/uso', {
        params: {
          horas_disponibles_param: horasDisponiblesParam || 24,
        },
      });
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  /**
   * Obtiene datos de consumo por insumo
   */
  async obtenerDatosConsumo(
    fechaInicio: string,
    fechaFin: string
  ): Promise<DatosConsumo[]> {
    try {
      const response = await apiClient.get<DatosConsumo[]>('/gestion/consumo', {
        params: {
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      return response.data;
    } catch (error: any) {
      return [];
    }
  }

  /**
   * Obtiene resumen de operaciones
   */
  async obtenerResumenOperacion(): Promise<ResumenMaquina> {
    try {
      const response = await apiClient.get<ResumenMaquina>('/gestion/resumen-operacion');
      return response.data;
    } catch (error: any) {
      return {
        total_horas: 0,
        maquinas_activas: 0,
        horas_promedio: 0,
      };
    }
  }

  /**
   * Obtiene resumen de consumos
   */
  async obtenerResumenConsumo(
    fechaInicio: string,
    fechaFin: string
  ): Promise<ResumenConsumo> {
    try {
      const response = await apiClient.get<ResumenConsumo>('/gestion/resumen-consumo', {
        params: {
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        },
      });
      return response.data;
    } catch (error: any) {
      return {
        total_consumido: 0,
        insumo_mas_consumido: 'N/A',
        cantidad_mas_consumida: 0,
      };
    }
  }

  /**
   * Obtiene datos de eficiencia de combustible para gráfico de burbuja
   */
  async obtenerDatosEficienciaCombustible(
    fechaInicio: string,
    fechaFin: string,
    maquinaId?: number,
    operadorId?: number,
    faenaId?: number
  ): Promise<DatosEficienciaCombustible[]> {
    try {
      const response = await apiClient.get<DatosEficienciaCombustible[]>(
        '/gestion/eficiencia-combustible',
        {
          params: {
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            ...(maquinaId && { maquina_id: maquinaId }),
            ...(operadorId && { operador_id: operadorId }),
            ...(faenaId && { faena_id: faenaId }),
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return [];
    }
  }
}

export default new GestionService();
